import { Action, createAction, handleActions } from 'redux-actions';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { find } from 'lodash';

import { AxiosError, AxiosRequestConfig } from 'axios';
import { InvoiceAuthModel } from '../../../models/invoice-auth.model';
import { Invoice } from '../../../models/invoice.model';
import { Merchant } from '../../../models/merchant.model';
import { Payment } from '../../../models/payment.model';
import { State } from '../../../models/state.model';
import { CustomAxios } from '../../axios/axios';
import { convertToCents } from '../../../helpers/currency';

interface InvoiceRequestPayload {
  uniqueLink: string;
  birthdate?: string;
}

interface InvoiceResponsePayload {
  invoice: Invoice;
  attachment?: Blob;
}

interface PaymentPayload {
  invoiceId: string;
  payment: Payment;
  complete?: (reason?: 'success' | 'fail' | 'unknown') => void;
}

export const getInvoice = createAction<InvoiceRequestPayload>('GET_INVOICE');
export const getInvoiceSuccess = createAction<InvoiceResponsePayload>(
  'GET_INVOICE_SUCCESS'
);
export const getInvoiceFailure = createAction<Error>('GET_INVOICE_FAILURE');

export const getMerchant = createAction<string>('GET_MERCHANT');
export const getMerchantSuccess = createAction<Merchant>('GET_MERCHANT_SUCCESS');
export const getMerchantFailure = createAction<Error>('GET_MERCHANT_FAILURE');

export const getMerchantLogo = createAction<string>('GET_MERCHANT_LOGO');
export const getMerchantLogoSuccess = createAction<string>('GET_MERCHANT_LOGO_SUCCESS');

export const submitPayment = createAction<PaymentPayload>('SUBMIT_PAYMENT');
export const submitPaymentSuccess = createAction('SUBMIT_PAYMENT_SUCCESS');
export const submitPaymentFailure = createAction<Error>('SUBMIT_PAYMENT_FAILURE');
export const cardDeclined = createAction<Error>('CARD_DECLINED');

export const selectInvoice = (state: State) => state.payment.invoice;
export const selectInvoiceError = (state: State) => state.payment.error;
export const selectInvoiceLogo = (state: State) => state.payment.logo;
export const selectInvoiceMerchant = (state: State) => state.payment.merchant;
export const selectInvoiceAttachment = (state: State) => state.payment.attachment;
export const selectInvoiceAuth = (state: State) => state.payment.auth;
export const selectPaymentSuccess = (state: State) => state.payment.success;
export const selectPaymentLoading = (state: State) => state.payment.loading;
export const selectDeclined = (state: State) => state.payment.declined;

const getUrlParams = () => {
  try {
    const queryString = window.location.search;
    const token = new URLSearchParams(queryString).get('token');
    const locationId = new URLSearchParams(queryString).get('locationId');
    return {
      token: decodeURIComponent(token || ''),
      locationId: decodeURIComponent(locationId || ''),
    };
  } catch {
    return { token: '', locationId: '' };
  }
};

export const handleGetMerchant = function*(action: Action<number>) {
  try {
    const invoiceId = action.payload;
    const response = yield call(CustomAxios.get, `invoices/${invoiceId}/merchant`);
    const merchant: Merchant = response.data.data;
    const activeProcessor = find(merchant.processors, 'isActive');

    // Prevent taking payments if their Stripe account is disabled
    if (!(activeProcessor && activeProcessor.chargesEnabled)) {
      throw { response: { data: { error: { codeId: 99 } } } };
    }

    yield put(getMerchantSuccess(merchant));
  } catch (error) {
    yield put(getMerchantFailure(error));
  }
};

export const handleGetMerchantLogo = function*(action: Action<number>) {
  try {
    const invoiceId = action.payload;
    const response = yield call(CustomAxios.get, `invoices/${invoiceId}/merchant/logo`, {
      responseType: 'arraybuffer',
    });
    const type = response.headers['content-type'];
    const data = new Buffer(response.data, 'binary').toString('base64');
    const logo = `data:${type};base64,${data}`;

    yield put(getMerchantLogoSuccess(logo));
  } catch (error) {
    console.error('Error loading logo', error);
  }
};

export const handleGetInvoice = function*(action: Action<InvoiceRequestPayload>) {
  try {
    let config: AxiosRequestConfig;
    const { uniqueLink, birthdate } = action.payload!;
    const { token, locationId } = getUrlParams();

    if (token) {
      config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Location-Id': locationId,
        },
      };
    } else {
      config = {
        auth: {
          username: 'birthday',
          password: birthdate || '',
        },
      };
    }

    const invoiceResponse = yield call(CustomAxios.get, `invoices/${uniqueLink}`, config);
    const invoice: Invoice = {
      ...invoiceResponse.data.data,
      amount: invoiceResponse.data.data.amount / 100,
    };

    yield put(getMerchant(invoice.id));
    yield put(getMerchantLogo(invoice.id));

    const payload: InvoiceResponsePayload = {
      invoice,
    };

    if (invoice.hasAttachment) {
      try {
        const attachmentResponse = yield call(
          CustomAxios.get,
          `invoices/${invoice.id}/attachment`,
          { ...config, responseType: 'blob' }
        );
        payload.attachment = attachmentResponse.data;
      } catch (error) {
        console.error(error);
      }
    }

    yield put(getInvoiceSuccess(payload));
  } catch (error) {
    // If it's an authentication error, we will still get the invoice id so we can fetch their logo
    if (
      error.response &&
      error.response.status === 401 &&
      error.response.data &&
      error.response.data.invoiceId
    ) {
      const invoiceId = error.response.data.invoiceId;
      yield put(getMerchant(invoiceId));
      yield put(getMerchantLogo(invoiceId));
    }

    if (action.payload && action.payload.birthdate) {
      error.response.data.authAttempted = true;
    }

    yield put(getInvoiceFailure(error));
  }
};

export const handleSubmitPayment = function*(action: Action<PaymentPayload>) {
  try {
    if (action.payload.payment.amount > 0) {
      const amount = convertToCents(action.payload.payment.amount);
      const payment = { ...action.payload.payment, amount };
      yield call(
        CustomAxios.post,
        `invoices/${action.payload.invoiceId}/payments`,
        payment
      );
      yield put(submitPaymentSuccess());
      if (action.payload.complete) {
        yield call(action.payload.complete, 'success');
      }
    } else {
      throw new Error('Amount must be a positive number');
    }
  } catch (error) {
    if (error && error.response && error.response.status === 402) {
      yield put(cardDeclined(error));
    } else {
      yield put(submitPaymentFailure(error));
    }
    if (action.payload && action.payload.complete) {
      yield call(action.payload.complete, 'fail');
    }
  }
};

export const paymentSaga = function*() {
  yield all([
    takeEvery(getInvoice.toString(), handleGetInvoice),
    takeEvery(getMerchant.toString(), handleGetMerchant),
    takeEvery(getMerchantLogo.toString(), handleGetMerchantLogo),
    takeEvery(submitPayment.toString(), handleSubmitPayment),
  ]);
};

export interface PaymentStateModel {
  loading: boolean;
  invoice?: Invoice;
  logo: string;
  error?: AxiosError;
  success: boolean;
  merchant?: Merchant;
  attachment?: Blob;
  auth: InvoiceAuthModel;
  declined: boolean;
}

const defaultState: PaymentStateModel = {
  loading: false,
  logo: '',
  success: false,
  declined: false,
  auth: {
    required: false,
    name: '',
    lockedOut: false,
    attempted: false,
  },
};

export const paymentReducer = handleActions<PaymentStateModel, any>(
  {
    [getInvoice.toString()]: (state) =>
      Object.assign({}, state, {
        auth: defaultState.auth,
        invoice: undefined,
        loading: true,
      }),
    [getInvoiceSuccess.toString()]: (state, action: Action<InvoiceResponsePayload>) =>
      Object.assign({}, state, {
        invoice: action.payload!.invoice,
        attachment: action.payload!.attachment,

        loading: false,
        error: null,
      }),
    [getInvoiceFailure.toString()]: (state, action: Action<AxiosError>) => {
      const error = action.payload!;
      const auth: InvoiceAuthModel = {
        required: false,
        attempted: false,
        lockedOut: false,
        name: '',
      };

      if (error.response && error.response.status === 401) {
        auth.required = true;
        auth.name = `${error.response.data.firstName} ${error.response.data.lastName}.`;
        auth.lockedOut = error.response.data.locked;
        auth.attempted = error.response.data.authAttempted;
      }

      return Object.assign({}, state, { error, auth, loading: false });
    },
    [getMerchantSuccess.toString()]: (state, action: Action<Merchant>) =>
      Object.assign({}, state, { merchant: action.payload }),
    [getMerchantFailure.toString()]: (state, action: Action<Error>) =>
      Object.assign({}, state, { error: action.payload }),
    [getMerchantLogoSuccess.toString()]: (state, action: Action<Merchant>) =>
      Object.assign({}, state, { logo: action.payload }),
    [submitPayment.toString()]: (state) =>
      Object.assign({}, state, { declined: false, loading: true, error: null }),
    [submitPaymentSuccess.toString()]: (state) =>
      Object.assign({}, state, { success: true, loading: false }),
    [submitPaymentFailure.toString()]: (state, action: Action<Error>) =>
      Object.assign({}, state, { error: action.payload, loading: false }),
    [cardDeclined.toString()]: (state, action: Action<Error>) =>
      Object.assign({}, state, { declined: true, loading: false, error: action.payload }),
  },
  defaultState
);
